export const MIN_AREA_SHADE_RADIUS_METERS = 500;
export const MAX_AREA_SHADE_RADIUS_METERS = 20_000;

const METERS_PER_DEGREE_LAT = 111_320;

export type AreaShadeBBox = {
  sw: { lat: number; lng: number };
  ne: { lat: number; lng: number };
};

export type AreaShade = {
  id: number;
  center: { lat: number; lng: number };
  radiusMeters: number;
};

export type AreaShadeMap = {
  on: (event: "remove", listener: () => void) => unknown;
  off: (event: "remove", listener: () => void) => unknown;
  addSource: (id: string, source: { type: "geojson"; data: unknown }) => void;
  getSource: (id: string) => { setData: (data: unknown) => void } | undefined;
  addLayer: (layer: Record<string, unknown>) => void;
  removeLayer: (id: string) => void;
  removeSource: (id: string) => void;
  setFeatureState: (
    target: { source: string; id: string | number },
    state: Record<string, unknown>,
  ) => void;
};

export type AreaShadeLayer = {
  setAreas: (areas: AreaShade[]) => void;
  setHovered: (id: number | null) => void;
  remove: () => void;
};

const layerCleanups = new WeakMap<AreaShadeMap, Map<string, () => void>>();

function spanMeters(bbox: AreaShadeBBox): { lat: number; lng: number } {
  const centerLat = (bbox.sw.lat + bbox.ne.lat) / 2;
  return {
    lat: Math.abs(bbox.ne.lat - bbox.sw.lat) * METERS_PER_DEGREE_LAT,
    lng:
      Math.abs(bbox.ne.lng - bbox.sw.lng) *
      METERS_PER_DEGREE_LAT *
      Math.cos((centerLat * Math.PI) / 180),
  };
}

/**
 * Half the place's smaller span, clamped. Returns null when the place is
 * larger than the viewport, so a county viewed inside a city stays a pill.
 */
export function shadeRadiusMeters(input: {
  bbox: AreaShadeBBox | null;
  viewport: AreaShadeBBox | null;
}): number | null {
  if (!input.bbox) return MIN_AREA_SHADE_RADIUS_METERS;

  const place = spanMeters(input.bbox);
  const smaller = Math.min(place.lat, place.lng);
  if (input.viewport) {
    const view = spanMeters(input.viewport);
    if (smaller > Math.min(view.lat, view.lng)) return null;
  }

  const radius = smaller / 2;
  if (radius <= 0) return MIN_AREA_SHADE_RADIUS_METERS;
  return Math.min(
    MAX_AREA_SHADE_RADIUS_METERS,
    Math.max(MIN_AREA_SHADE_RADIUS_METERS, radius),
  );
}

export function circlePolygon(
  center: { lat: number; lng: number },
  radiusMeters: number,
  steps = 48,
): { type: "Polygon"; coordinates: [number, number][][] } {
  const latRad = (center.lat * Math.PI) / 180;
  const metersPerDegreeLng = Math.max(
    METERS_PER_DEGREE_LAT * Math.cos(latRad),
    1,
  );
  const ring: [number, number][] = [];

  for (let step = 0; step <= steps; step += 1) {
    const angle = (step / steps) * Math.PI * 2;
    // Counterclockwise so Mapbox fills the ring instead of treating it as a hole.
    ring.push([
      center.lng - (radiusMeters * Math.sin(angle)) / metersPerDegreeLng,
      center.lat + (radiusMeters * Math.cos(angle)) / METERS_PER_DEGREE_LAT,
    ]);
  }

  return { type: "Polygon", coordinates: [ring] };
}

function shadeCollection(areas: AreaShade[]) {
  return {
    type: "FeatureCollection" as const,
    features: areas.map((area) => ({
      type: "Feature" as const,
      id: area.id,
      properties: {},
      geometry: circlePolygon(area.center, area.radiusMeters),
    })),
  };
}

export function addAreaShadeLayer(
  map: AreaShadeMap,
  { sourceId }: { sourceId: string },
): AreaShadeLayer {
  const fillLayerId = `${sourceId}-fill`;
  const lineLayerId = `${sourceId}-line`;
  let hoveredId: number | null = null;
  let removed = false;

  layerCleanups.get(map)?.get(sourceId)?.();

  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: "geojson",
      data: shadeCollection([]),
    });
    map.addLayer({
      id: fillLayerId,
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": "#059669",
        "fill-opacity": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          0.22,
          0.1,
        ],
      },
    });
    map.addLayer({
      id: lineLayerId,
      type: "line",
      source: sourceId,
      paint: {
        "line-color": "#047857",
        "line-width": 1.5,
        "line-dasharray": [2, 1.5],
      },
    });
  }

  const onMapRemoved = () => {
    removed = true;
    unregister();
  };
  const unregister = () => {
    map.off("remove", onMapRemoved);
    const registered = layerCleanups.get(map);
    if (registered?.get(sourceId) === remove) {
      registered.delete(sourceId);
    }
  };
  const remove = () => {
    if (removed) return;
    removed = true;
    unregister();
    try {
      map.removeLayer(lineLayerId);
      map.removeLayer(fillLayerId);
      map.removeSource(sourceId);
    } catch {
      // The map may already have been removed.
    }
  };
  map.on("remove", onMapRemoved);
  const bySource = layerCleanups.get(map) ?? new Map<string, () => void>();
  bySource.set(sourceId, remove);
  layerCleanups.set(map, bySource);

  return {
    setAreas(areas) {
      if (removed) return;
      map.getSource(sourceId)?.setData(shadeCollection(areas));
    },
    setHovered(id) {
      if (removed) return;
      if (hoveredId != null) {
        map.setFeatureState(
          { source: sourceId, id: hoveredId },
          { hover: false },
        );
      }
      hoveredId = id;
      if (id != null) {
        map.setFeatureState({ source: sourceId, id }, { hover: true });
      }
    },
    remove,
  };
}
